import axios from 'axios';
import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Duplicados de cliente (GESCL-T07, tramo 5) — contrato del backend
// (ClientesDuplicadosController + DTOs de Application/Altas/Duplicados).
// No modificar sin coordinar con el backend.
// ═══════════════════════════════════════════════════════════════

/** Resultado de la regla dura del superviviente (D-GC-34). */
export type ResultadoRegla = 'BLOQUEO_CU2' | 'FORZADO_CU2' | 'BLOQUEO_FARMACIA' | 'FORZADO_FARMACIA' | 'LIBRE';

/** Regla de premarca de un campo del catálogo de fusión (§5). */
export type ReglaCampo =
  | 'SI_VACIO' | 'CERO_ES_DATO' | 'VACIO_SI_NO_POSITIVO' | 'MAS_ANTIGUA' | 'CONCATENAR' | 'NUNCA' | 'DERIVADO'
  /** La fila se enseña pero el motor no la fusiona campo a campo (bloques e informativos). */
  | 'SOLO_LECTURA';

/**
 * De dónde sale cada fila de la rejilla y qué se puede hacer con ella (D-GC-53, script 029):
 * `CAMPO` la decide el operador · `BLOQUE` viaja entera con su tabla 1:1 y se decide el bloque ·
 * `INFORMATIVO` se mira y no se decide · `LISTA` no es una columna, es el hueco donde va una de
 * las cinco listas, en su sitio dentro del orden.
 */
export type OrigenCampo = 'CAMPO' | 'BLOQUE' | 'INFORMATIVO' | 'LISTA';

export type ComparacionCampo = 'VACIOS' | 'SOLO_ELIMINA' | 'SOLO_CONSERVA' | 'IGUAL' | 'DIFIERE';

export type TipoMultivalor = 'EMAIL' | 'TELEFONO' | 'FARMACIA' | 'PREF_PROVINCIA' | 'PREF_TRAMO';

/** Control con el que se edita una celda de la columna izquierda (script 030). */
export type TipoDato = 'TEXTO' | 'TEXTO_LARGO' | 'NUMERO' | 'FECHA' | 'FECHA_HORA' | 'BIT' | 'CATALOGO';

/** Los tres casos de la cuenta web (§4.7). */
export type CasoCuentaWeb = 'NADA' | 'TRASPASAR' | 'UNIR';

/** 1 SIMULADA · 2 APLICADA · 3 NO_LIMPIA · 4 ERROR. */
export enum EstadoFusion {
  Simulada = 1,
  Aplicada = 2,
  NoLimpia = 3,
  Error = 4,
}

export interface DuplicadoCandidato {
  idCliente: number;
  codigo: string | null;
  nombreCompleto: string | null;
  nif: string | null;
  provincia: string | null;
  fechaAlta: string | null;
  titularFarmacia: boolean;
  /** No nulo = esta ficha NO se puede eliminar (D-GC-34). */
  idColegiadoCu2: number | null;
}

export interface FichaSenales {
  /** 1 = se conserva · 2 = se elimina. Con bloqueo, se mantiene el lado de entrada. */
  lado: 1 | 2;
  ladoEntrada: 1 | 2;
  idCliente: number;
  codigo: string | null;
  prefijo: 'CL' | 'PR' | 'OTRO' | null;
  nombreCompleto: string | null;
  nif: string | null;
  idColegiadoCu2: number | null;
  titularFarmacia: boolean;
  numeroFarmacias: number;
  idClienteGf: number | null;
  idClienteWeb: number | null;
  fechaAlta: string | null;
  fechaUltimaModificacion: string | null;
  camposInformados: number;
  numeroEmails: number;
  numeroTelefonos: number;
  numeroDirecciones: number;
  fichaCompraVigente: boolean;
  fechaFichaCompra: string | null;
}

export interface ReglaSuperviviente {
  resultado: ResultadoRegla;
  /** Nulo en los dos bloqueos. */
  idClienteConservar: number | null;
  bloqueo: boolean;
  forzado: boolean;
  explicacion: string | null;
  avisoCu2SinPoblar: boolean;
  avisoFarmaciaSinPoblar: boolean;
}

export interface CampoEnfrentado {
  tabla: string;
  campo: string;
  etiqueta: string;
  grupo: string;
  orden: number;
  regla: ReglaCampo;
  origen: OrigenCampo;
  /** Se pinta en la rejilla. Las que no, siguen contando para las señales del paso 2. */
  mostrar: boolean;
  /** Solo en `BLOQUE`: la tabla que hay que nombrar al decidir el bloque (D-GC-43). */
  tablaBloque: string | null;
  /** Catálogo con el que se traduce y se elige el valor (`GC_C_SUBREGION`, `GC_C_PROFESION`…). */
  catalogo: string | null;
  /** Control con el que se edita la celda. Nulo en las listas. */
  tipoDato: TipoDato | null;
  /** Longitud máxima del texto, si la columna la tiene. */
  longitud: number | null;
  /**
   * El operador puede escribir en la celda (script 030). Falso en identificadores, calculados,
   * fecha de baja, fechas de la ficha de compra y farmacia. La premarca y «restaurar» valen igual.
   */
  editable: boolean;
  alarma: boolean;
  clave: boolean;
  valorConserva: string | null;
  valorElimina: string | null;
  descripcionConserva: string | null;
  descripcionElimina: string | null;
  comparacion: ComparacionCampo | null;
  /**
   * La regla dice que gana el valor de la ficha que se elimina. Desde el script 030 también en
   * bloques (dirección, exploraciones), ficha de compra y preferencias: es lo que hará el motor.
   */
  premarcaElimina: boolean;
  avisoClave: boolean;
  claveCoincide: boolean;
  alarmaActiva: boolean;
  textoSucio: boolean;
}

export interface ElementoMultivalor {
  lado: 1 | 2;
  tipo: TipoMultivalor;
  idElemento: number;
  valor: string | null;
  principal: boolean | null;
  /** Teléfono: el tipo (Móvil/Fijo/Otros). Farmacia: `Dirección - Población`. Tramo: su código. */
  detalle: string | null;
  /** Id de catálogo: el tipo en teléfono, la provincia o el tramo en las preferencias. */
  idCatalogo: number | null;
  /**
   * El otro lado tiene el mismo valor: en email y teléfono, que no se duplicará al fusionar;
   * en farmacia, que los dos son titulares de la misma.
   */
  repetido: boolean;
}

export interface TitularidadImplicada {
  ladoEntrada: 1 | 2;
  idCliente: number;
  idRelacion: string | null;
  idEntidad: number | null;
  /** Id de Gescom de la farmacia: `ID_CODIGO_GF` está vacío en las 24.339 de la base. */
  codigoFarmacia: string | null;
  farmacia: string | null;
  /** `Dirección - Población` de la farmacia. */
  ubicacion: string | null;
  fechaInicio: string | null;
  /** Nula = titularidad vigente, que es la que bloquea. */
  fechaFin: string | null;
}

export interface ComparacionDuplicados {
  fichas: FichaSenales[];
  regla: ReglaSuperviviente;
  campos: CampoEnfrentado[];
  multivalor: ElementoMultivalor[];
  titularidades: TitularidadImplicada[];
}

export interface EleccionCampo {
  tabla: string;
  /** Nombre del campo, o `*` para decidir un bloque 1:1 entero (D-GC-43). */
  campo: string;
  tomarDelDuplicado: boolean;
}

/** Un campo con el valor que el operador ha escrito en la columna izquierda (script 030). */
export interface ValorEditado {
  tabla: string;
  campo: string;
  /** Texto tal como lo devuelve comparar (ids como texto, fechas ISO). Nulo = vaciar. */
  valor: string | null;
}

export type TipoElementoEditable = 'EMAIL' | 'TELEFONO' | 'PREF_PROVINCIA' | 'PREF_TRAMO';
export type AccionSobreElemento = 'ELIMINAR' | 'EDITAR' | 'PRINCIPAL' | 'ANADIR' | 'QUITAR';

/** Una acción del operador sobre un elemento de lista (script 030). */
export interface AccionElemento {
  tipo: TipoElementoEditable;
  accion: AccionSobreElemento;
  /** `ID_EMAIL`/`ID_TELEFONO`; en preferencias el id de catálogo. En `PRINCIPAL`, nulo = ninguno. */
  idElemento: number | null;
  /** Solo `EDITAR`: el nuevo email o número. */
  valor?: string | null;
  /** Solo `EDITAR` de teléfono: el tipo. */
  idTipo?: number | null;
}

export interface FusionRequest {
  idClienteConservar: number;
  idClienteEliminar: number;
  /** Obligatorio al aplicar (mín. 10 caracteres, máx. 500). */
  motivo?: string | null;
  elecciones?: EleccionCampo[];
  valores?: ValorEditado[];
  elementos?: AccionElemento[];
  /**
   * Solo al aplicar, y obligatorio (D-GC-87): el `idClienteFusion` de la simulación de ESTA MISMA
   * fusión. El servidor la rechaza si es de otro operador, de otra pareja, con otra edición o de
   * hace más de 30 minutos.
   */
  idSimulacion?: number | null;
}

/** Una fusión APLICADA cuyo duplicado sigue vivo: hay que terminarla, no repetirla (S-03/S-19). */
export interface FusionPendiente {
  idClienteFusion: number;
  idClienteConservado: number;
  codigoConservado: string | null;
  idClienteEliminado: number;
  codigoEliminado: string | null;
  fechaAplicacion: string | null;
  usuarioAlta: string | null;
  /** La simulación que autorizó aplicarla (D-GC-87): si no es la mía, la fusión es de otro (N-03). */
  idFusionSimulada: number | null;
  cuentaWebResuelta: boolean;
}

/** Un elemento de los catálogos de los desplegables. `catalogo` casa con `CampoEnfrentado.catalogo`. */
export interface ElementoCatalogo {
  catalogo: string;
  id: number;
  descripcion: string;
  codigo: string | null;
}

export interface CampoFusionado {
  tabla: string;
  campo: string;
  valorAntes: string | null;
  valorDuplicado: string | null;
  valorDespues: string | null;
  origen: 'REGLA' | 'OPERADOR';
}

export interface TraspasoTabla {
  tabla: string;
  /** Columna, o el pseudo-nombre `(repetidos)` / `(historificadas)`. */
  columna: string;
  traspasadas: number;
  descartadas: number;
}

export interface ReferenciaPendiente {
  tabla: string;
  columna: string;
  tipoReferencia: string | null;
  origen: 'FK' | 'CATALOGO' | null;
  estrategia: 'LIBRE' | 'COMPUESTA' | 'BLOQUE' | null;
  filasEliminar: number;
  filasConservar: number | null;
  observaciones: string | null;
}

export interface CuentaWebFusion {
  idClienteFusion: number | null;
  idClienteConservado: number;
  idClienteEliminado: number;
  caso: CasoCuentaWeb;
  idClienteWebConservar: number | null;
  idClienteWebEliminar: number | null;
  idClienteWebOrigen: number | null;
  codigoConservado: string | null;
  resuelto: boolean;
  resultado: string | null;
}

export interface ResultadoFusion {
  idClienteFusion: number;
  idEstado: EstadoFusion;
  estado: string;
  confirmado: boolean;
  idClienteConservado: number;
  codigoConservado: string | null;
  idClienteEliminado: number;
  codigoEliminado: string | null;
  campos: CampoFusionado[];
  traspasos: TraspasoTabla[];
  /** VACÍO es la condición que autoriza a eliminar (D-GC-36). */
  pendientes: ReferenciaPendiente[];
  cuentaWeb: CuentaWebFusion | null;
}

export interface ResultadoCuentaWeb {
  idClienteFusion: number;
  caso: CasoCuentaWeb;
  idClienteWebConservado: number | null;
  idClienteWebArchivado: number | null;
  visitasReasignadas: number;
  /** true = no se llamó a GFW porque el paso ya estaba hecho. */
  yaEstaba: boolean;
  resultado: string;
}

export interface ResultadoEliminacion {
  idClienteFusion: number;
  idClienteEliminado: number;
  personaEliminada: boolean;
  personaConservadaPor: string | null;
}

export interface InformeFusion {
  idClienteFusion: number;
  idClienteConservado: number;
  codigoConservado: string | null;
  idClienteEliminado: number;
  codigoEliminado: string | null;
  nombreEliminado: string | null;
  idEstado: EstadoFusion;
  estado: string;
  motivo: string | null;
  reglaSuperviviente: string | null;
  personaEliminada: boolean | null;
  personaConservadaPor: string | null;
  resultadoGfw: string | null;
  conError: boolean;
  fechaSimulacion: string | null;
  fechaAplicacion: string | null;
  fechaAlta: string;
  usuarioAlta: string;
  campos: CampoFusionado[];
  traspasos: TraspasoTabla[];
}

// ─── Llamadas ───────────────────────────────────────────────────

const BASE = '/api/clientes/duplicados';

/**
 * Las llamadas que tocan el motor esperan 240 s (S-03 de la revisión BE/FE de T08): por encima del
 * `CommandTimeout` del servidor (200 s) y del corte de App Service (230 s). Así, si el servidor se
 * rinde, la respuesta llega; y si no llega ninguna, la pantalla sabe que no puede dar por hecho
 * que no se aplicó y reconcilia antes de reintentar.
 */
const TIMEOUT_MOTOR = 240_000;

export const buscarCandidatos = async (
  busqueda: string,
  excluir?: number,
  maximo = 25,
): Promise<DuplicadoCandidato[]> => {
  const res = await api.get(`${BASE}/buscar`, { params: { busqueda, excluir, maximo } });
  return res.data;
};

export const compararClientes = async (cliente1: number, cliente2: number): Promise<ComparacionDuplicados> => {
  const res = await api.get(`${BASE}/comparar`, { params: { cliente1, cliente2 } });
  return res.data;
};

export const leerCatalogosDuplicados = async (): Promise<ElementoCatalogo[]> => {
  const res = await api.get(`${BASE}/catalogos`);
  return res.data;
};

export const simularFusion = async (req: FusionRequest): Promise<ResultadoFusion> => {
  const res = await api.post(`${BASE}/simular`, req, { timeout: TIMEOUT_MOTOR });
  return res.data;
};

export const aplicarFusion = async (req: FusionRequest): Promise<ResultadoFusion> => {
  const res = await api.post(`${BASE}/aplicar`, req, { timeout: TIMEOUT_MOTOR });
  return res.data;
};

export const resolverCuentaWeb = async (idFusion: number): Promise<ResultadoCuentaWeb> => {
  const res = await api.post(`${BASE}/fusiones/${idFusion}/cuenta-web`, undefined, { timeout: TIMEOUT_MOTOR });
  return res.data;
};

export const eliminarDuplicado = async (idFusion: number): Promise<ResultadoEliminacion> => {
  const res = await api.post(`${BASE}/fusiones/${idFusion}/eliminar`, undefined, { timeout: TIMEOUT_MOTOR });
  return res.data;
};

/** Fusiones aplicadas sin terminar sobre la pareja (S-03/S-19): lo que se busca antes de reintentar. */
export const buscarFusionesPendientes = async (cliente1: number, cliente2: number): Promise<FusionPendiente[]> => {
  const res = await api.get(`${BASE}/fusiones/pendientes`, { params: { cliente1, cliente2 } });
  return res.data;
};

export const leerInformeFusion = async (idFusion: number): Promise<InformeFusion> => {
  const res = await api.get(`${BASE}/fusiones/${idFusion}`);
  return res.data;
};

// ─── Errores ────────────────────────────────────────────────────

/** Motivo de la barrera del superviviente (409 «Regla del superviviente»). */
export type MotivoBarrera = 'AMBOS_CU2' | 'ELIMINA_CU2' | 'AMBOS_TITULARES' | 'ELIMINA_TITULAR';

export interface ErrorDuplicados {
  status?: number;
  titulo?: string;
  mensaje: string;
  /** Errores de validación (400) en lista. */
  faltas?: string[];
  /** Solo en el 409 de la barrera del superviviente. */
  motivo?: MotivoBarrera;
  seResuelveIntercambiando?: boolean;
  /** Solo en el 422 «Quedan referencias al cliente». */
  pendientes?: ReferenciaPendiente[];
  /** Solo en el 409 «Fusión sin terminar»: la que hay que reanudar (S-03/S-19). */
  idFusionPendiente?: number | null;
  /** Solo en el 409 de una fusión ya completada: la respuesta anterior se perdió, no es un fallo (S-13). */
  yaCompletada?: boolean;
  /** Solo en el 409 de aplicar sin simulación válida (D-GC-87): basta con volver a simular. */
  volverASimular?: boolean;
  /**
   * No llegó respuesta (red caída o timeout). Tras `aplicar`, NO quiere decir que no se aplicara:
   * hay que reconciliar antes de reintentar (S-03).
   */
  sinRespuesta?: boolean;
}

const MENSAJE_GENERICO = 'No se ha podido completar la operación. Inténtalo de nuevo en unos minutos.';

/**
 * Traduce los ProblemDetails del módulo (ver ClientesDuplicadosController) a algo que la
 * pantalla pueda enseñar. Igual que `interpretarError` de altas: el `detail` de un 5xx nunca
 * se vuelca (RF-13), solo los 4xx, que están redactados para el operador.
 */
export const interpretarErrorDuplicados = (error: unknown): ErrorDuplicados => {
  if (!axios.isAxiosError(error)) return { mensaje: MENSAJE_GENERICO };
  if (!error.response) {
    return { mensaje: 'No hay respuesta del servidor. Comprueba la conexión e inténtalo de nuevo.', sinRespuesta: true };
  }
  const { status, data } = error.response as {
    status: number;
    data?: {
      title?: string; detail?: string; error?: string;
      errors?: Record<string, string[]>;
      motivo?: MotivoBarrera; seResuelveIntercambiando?: boolean;
      pendientes?: ReferenciaPendiente[];
      idFusionPendiente?: number | null; yaCompletada?: boolean; volverASimular?: boolean;
    };
  };

  if (status === 400 && data?.errors) {
    const faltas = Object.values(data.errors).flat();
    if (faltas.length > 0) return { status, mensaje: 'Revisa los datos introducidos.', faltas };
  }
  if (status === 403) {
    return { status, titulo: 'Sin permiso', mensaje: 'No tienes permiso para tratar duplicados de clientes.' };
  }
  if (status === 409 && data?.motivo) {
    return {
      status,
      titulo: data.title ?? 'Regla del superviviente',
      mensaje: data.detail ?? 'La regla del superviviente no permite esta fusión.',
      motivo: data.motivo,
      seResuelveIntercambiando: data.seResuelveIntercambiando === true,
    };
  }
  if (status === 422 && Array.isArray(data?.pendientes)) {
    return {
      status,
      titulo: data?.title ?? 'Quedan referencias al cliente',
      mensaje: data?.detail ?? 'No se ha eliminado nada.',
      pendientes: data!.pendientes,
    };
  }
  if (status === 503) {
    return {
      status,
      titulo: data?.title ?? 'Operación en curso',
      mensaje: data?.detail ?? 'Hay otra operación en curso sobre alguno de los dos clientes. Reinténtalo en unos segundos.',
    };
  }
  if (status >= 400 && status < 500) {
    return {
      status,
      titulo: data?.title,
      mensaje: data?.detail || data?.title || 'No se ha podido completar la operación.',
      idFusionPendiente: data?.idFusionPendiente ?? undefined,
      yaCompletada: data?.yaCompletada === true,
      volverASimular: data?.volverASimular === true,
    };
  }
  return { status, mensaje: MENSAJE_GENERICO };
};
